import { useState, useEffect } from "react";
import OwnerMenu from "./OwnerMenu";
import NavBar from "../common/NavBar";
import Footer from "../common/Footer";
import { useTranslation } from "react-i18next";
import { Tabs, Tab, Box } from "@mui/material";

const ProfilePage = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(0);
  
  // State to store user profile data
  const [profile, setProfile] = useState({
    username: "",
    first_name: "",
    last_name: "",
    email: "",
    groups: [],
    profile: {
      instagram_url: "",
      twitter_url: "",
      facebook_url: "",
    },
  });

  // State for API Communication settings
  const [apiSettings, setApiSettings] = useState({
    RAPIDAPI_KEY: "",
    RAPIDAPI_URL: "",
    RAPIDAPI_HOST: "",
  });

  // Determine the API base URL based on the environment
  const getApiBaseUrl = () => {
    // Use the REACT_APP_API_URL from .env, fallback to localhost if not set
    return process.env.REACT_APP_API_URL || "http://localhost:8000";
  };

  // Fetch user profile data on component mount
  useEffect(() => {
    const fetchProfile = async () => {
      // Retrieve user ID and authentication token from localStorage
      const userId = localStorage.getItem("user_id");
      const authToken = localStorage.getItem("token");

      if (!userId || !authToken) {
        console.error(t("missing_authentication_details"));
        return;
      }

      try {
        const response = await fetch(`${getApiBaseUrl()}/api/users/groups/owners/${userId}/`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Token ${authToken}`, // Send authentication token
          },
        });

        if (!response.ok) {
          throw new Error(`${t("error_fetching_profile")} (Status: ${response.status})`);
        }

        const data = await response.json();

        setProfile({
          username: data.username,
          first_name: data.first_name || "",
          last_name: data.last_name || "",
          email: data.email,
          groups: data.groups || [],
          profile: {
            instagram_url: data.profile?.instagram_url || "",
            twitter_url: data.profile?.twitter_url || "",
            facebook_url: data.profile?.facebook_url || "",
          },
        });

        // Store social links for the Footer
        localStorage.setItem("social_links", JSON.stringify({
          instagram_url: data.profile?.instagram_url || "",
          twitter_url: data.profile?.twitter_url || "",
          facebook_url: data.profile?.facebook_url || "",
        }));
      } catch (error) {
        console.error(t("error_fetching_profile"), error);
      }
    };

    fetchProfile();

    // Fetch API communication settings
    const fetchApiSettings = async () => {
      const authToken = localStorage.getItem("token");
      if (!authToken) return;
      try {
        const response = await fetch(`${getApiBaseUrl()}/api/properties/rapidapi-settings/`, {
          headers: { Authorization: `Token ${authToken}` },
        });
        if (response.ok) {
          const data = await response.json();
          setApiSettings(data);
        }
      } catch (error) {
        console.error("Error fetching API settings:", error);
      }
    };
    fetchApiSettings();
  }, [t]);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith("profile.")) {
      const field = name.split(".")[1];
      setProfile((prev) => ({
        ...prev,
        profile: { ...prev.profile, [field]: value },
      }));
    } else {
      setProfile((prevProfile) => ({
        ...prevProfile,
        [name]: value,
      }));
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    const userId = localStorage.getItem("user_id");
    const authToken = localStorage.getItem("token");

    if (!userId || !authToken) {
      alert(t("missing_authentication_details"));
      return;
    }

    try {
      const response = await fetch(`${getApiBaseUrl()}/api/users/groups/owners/${userId}/`, {
        method: "PUT", // or PATCH depending on your API
        headers: {
          "Content-Type": "application/json",
          Authorization: `Token ${authToken}`,
        },
        body: JSON.stringify(profile),
      });

      if (!response.ok) {
        throw new Error(t("failed_to_update_profile"));
      }

      // Update social links in localStorage for the Footer
      localStorage.setItem("social_links", JSON.stringify(profile.profile));

      alert(t("profile_updated_successfully"));
    } catch (error) {
      console.error(t("failed_to_update_profile"), error);
      alert(t("failed_to_update_profile"));
    }
  };

  // Handle API settings changes
  const handleApiChange = (e) => {
    const { name, value } = e.target;
    setApiSettings((prev) => ({ ...prev, [name]: value }));
  };

  // Handle API settings save
  const handleApiSubmit = async (e) => {
    e.preventDefault();
    const authToken = localStorage.getItem("token");
    if (!authToken) {
      alert(t("missing_authentication_details"));
      return;
    }
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/properties/rapidapi-settings/`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Token ${authToken}`,
        },
        body: JSON.stringify(apiSettings),
      });
      if (!response.ok) throw new Error(t("failed_to_update_profile"));
      alert(t("api_settings_updated"));
    } catch (error) {
      console.error("Error updating API settings:", error);
      alert(t("failed_to_update_profile"));
    }
  };

  return (
    <div className="flex flex-col min-h-screen">
      <NavBar />
      {/* Sidebar Menu */}
      <OwnerMenu />

      {/* Main Content */}
      <div className="ml-48 mt-16 p-6">
        <h1 className="text-3xl font-bold mb-4">{t("profile")}</h1>
        <p className="text-gray-600">{t("update_profile_info")}</p>

        <Box sx={{ borderBottom: 1, borderColor: "divider", mt: 3 }}>
          <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
            <Tab label={t("personal_information")} />
            <Tab label={t("social_networks")} />
            <Tab label={t("api_communication")} />
          </Tabs>
        </Box>

        <form onSubmit={handleSubmit} className="mt-6 space-y-4">
          {/* Tab 0: Personal Information */}
          {activeTab === 0 && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700">{t("username")}</label>
                <input
                  type="text"
                  name="username"
                  value={profile.username}
                  onChange={handleChange}
                  className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">{t("first_name")}</label>
                <input
                  type="text"
                  name="first_name"
                  value={profile.first_name}
                  onChange={handleChange}
                  className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">{t("last_name")}</label>
                <input
                  type="text"
                  name="last_name"
                  value={profile.last_name}
                  onChange={handleChange}
                  className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">{t("email")}</label>
                <input
                  type="email"
                  name="email"
                  value={profile.email}
                  onChange={handleChange}
                  className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
                />
              </div>
            </>
          )}

          {/* Tab 1: Social Networks */}
          {activeTab === 1 && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700">Instagram URL</label>
                <input
                  type="url"
                  name="profile.instagram_url"
                  value={profile.profile.instagram_url}
                  onChange={handleChange}
                  placeholder="https://instagram.com/..."
                  className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Twitter / X URL</label>
                <input
                  type="url"
                  name="profile.twitter_url"
                  value={profile.profile.twitter_url}
                  onChange={handleChange}
                  placeholder="https://x.com/..."
                  className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Facebook URL</label>
                <input
                  type="url"
                  name="profile.facebook_url"
                  value={profile.profile.facebook_url}
                  onChange={handleChange}
                  placeholder="https://facebook.com/..."
                  className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
                />
              </div>
            </>
          )}

          <button
            type="submit"
            className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600"
          >
            {t("update_profile")}
          </button>
        </form>

        {/* Tab 2: API Communication */}
        {activeTab === 2 && (
          <form onSubmit={handleApiSubmit} className="mt-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">{t("rapidapi_key")}</label>
              <input
                type="text"
                name="RAPIDAPI_KEY"
                value={apiSettings.RAPIDAPI_KEY}
                onChange={handleApiChange}
                className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">{t("rapidapi_url")}</label>
              <input
                type="url"
                name="RAPIDAPI_URL"
                value={apiSettings.RAPIDAPI_URL}
                onChange={handleApiChange}
                placeholder="https://booking-com15.p.rapidapi.com/api/v1/hotels/getAvailability"
                className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">{t("rapidapi_host")}</label>
              <input
                type="text"
                name="RAPIDAPI_HOST"
                value={apiSettings.RAPIDAPI_HOST}
                onChange={handleApiChange}
                placeholder="booking-com15.p.rapidapi.com"
                className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
              />
            </div>

            <button
              type="submit"
              className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600"
            >
              {t("update_api_settings")}
            </button>
          </form>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default ProfilePage;